<template>
  <modal
    :show="showModal"
    @close="handleClose"
  >
    <template v-slot:header>
      <p><b>Session Timeout</b></p>
    </template>
    <template v-slot:close-button></template>
    <template v-slot:default>
      <span v-html="message"></span>
    </template>
    <template v-if="!autoRenewed && countdown >= 0" v-slot:footer>
      <div v-if="countdown >= 0">
        <p>Your session expires in {{ formatTime(countdown) }}</p>
      </div>
    </template>
  </modal>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import Modal from './Modal.vue';
import { isNullOrEmpty } from '../modules/utils';

export default {
  name: 'session-timeout',
  components: {
    Modal
  },
  props: {
    maxAutoRenewal: {
      type: Number,
      default: 12,
      description: 'Maximum number of times session can be automatically renewed'
    },
    maxRenewal: {
      type: Number,
      default: 0,
      description: 'Maximum number of times session can be renewed. 0 - infinite (or very large number). Value equals maxAutoRenewal if it is <= maxAutoRenewal'
    },
    minSessionTimeoutMs: {
      type: Number,
      default: 29000,  // Default to 29s so the elapsed time can start countdown at 00:30, i.e., 30s will make the elapsed time start at 00:29.
      description: 'Minimum time to reach end of session in milliseconds'
    },
    notifyLeadTimeMs: {
      type: Number,
      default: 5 * 60000,  // 5 minutes in milliseconds
      description: 'Time from end of session to notify user of session timeout'
    },
    sessionIsActive: {
      type: Boolean,
      default: false,
      description: 'Whether the session is active'
    },
    notification: {
      type: String,
      default: "Your session is being renewed. Click the <i>Close</i> button or outside the notification box to continue.",
      description: 'A notification message'
    },
    notifySessionExpired: {
      type: String,
      default: "<p>Your session has expired!<p><p>Click the <i>Close</i> button or outside the notification box to re-login.<p>",
      description: 'A notification message'
    },
    alert: {
      type: String,
      default: "Your session is about to expire. Click the <i>Close</i> button or outside the notification box to continue.",
      description: 'An alert message'
    },
  },
  data() {
    return {
      timeConst: {
        daysMs: 86400000,
        daysMsInverse: 1 / 86400000,
        hrsMs: 3600000,
        hrsMsInverse: 1 / 3600000,
        minMs: 60000,
        minMsInverse: 1 / 60000,
        secMs: 1000,
        secMsInverse: 1 / 1000,
      },
      sessionTimer: null,
      intervalTimer: null,
      countdown: 0,
      autoRenewed: true,
      numberOfSessionsRenewed: 0,
      showModal: false,
      refreshSession: false,
    };
  },
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development';
    },
    canAutoRenew() {
      return this.numberOfSessionsRenewed < this.maxAutoRenewal;
    },
    maximumRenewals() {
      // Default to a really large number
      let renewals = 99999999;
      if (this.maxRenewal > 0) {
        renewals = this.maxRenewal;
        if (this.maxRenewal <= this.maxAutoRenewal) {
          renewals = this.maxAutoRenewal;
        }
      }

      return renewals;
    },
    message() {
      let msg = this.alert;
      if (this.countdown < 0) {
        msg = this.notifySessionExpired;
      }
      else if (this.autoRenewed) {
        msg = this.notification;
      }

      return msg;
    },
    ...mapGetters([
      'surveyUser',
    ])
  },
  methods: {
    sessionTimeLeft() {
      let validTo = Date.parse(this.surveyUser.validTo);
      let currentDate = Date.now();
      let dateDiff = validTo - currentDate;
      return dateDiff;
    },
    sessionExpired() {
      return this.sessionTimeLeft() <= 0;
    },
    setSessionTimeout(timeout) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = setTimeout(() => {
        this.refreshSession = true;
      }, timeout);
    },
    getTimeout() {
      let timeout = this.sessionTimeLeft() - this.notifyLeadTimeMs;
      if (timeout < this.minSessionTimeoutMs) {
        timeout = this.minSessionTimeoutMs;
      }
      return timeout;
    },
    setSession() {
      const timeout = this.getTimeout();
      this.setSessionTimeout(timeout);
    },
    clearTimers() {
      clearInterval(this.intervalTimer);
      clearTimeout(this.sessionTimer);
    },
    async requestNewToken() {
      const response = await this.refreshToken({
          surveyUrlId: this.$route.params.surveyId
        });

      this.numberOfSessionsRenewed++;
      this.setSession();
    },
    async handleClose() {
      this.showModal = false;
      clearInterval(this.intervalTimer);
      const timeExpired = this.sessionExpired();
      if (timeExpired) {
        this.clearTimers();
        // If we emit immediately, the modal doesn't seem to have time to close completely to reset the screen.
        // So, wait at least 1ms to let the modal close cleanly before we redirect to the login screen.
        setTimeout(() => {
          this.$emit('sessionExpired');
        }, 10);
        return;
      }

      if (!this.autoRenewed && this.numberOfSessionsRenewed < this.maximumRenewals) {
        await this.requestNewToken();
      }
    },
    padTo2Digits(number) {
      return number.toString().padStart(2, '0');
    },
    formatTime(timeMs) {
      if (timeMs < 0) {
        return "";
      }
      
      let days = Math.floor(timeMs * this.timeConst.daysMsInverse);
      let dd = days > 0 ? days : "";
      let diff = timeMs - days * this.timeConst.daysMs;

      let hours = Math.floor(diff * this.timeConst.hrsMsInverse);
      let hh = hours > 0 ? this.padTo2Digits(hours) : "";
      diff -= hours * this.timeConst.hrsMs;

      let mins = Math.floor(diff * this.timeConst.minMsInverse);
      let mm = this.padTo2Digits(mins);
      diff -= mins * this.timeConst.minMs;

      let seconds = Math.floor(diff * this.timeConst.secMsInverse);
      let ss = this.padTo2Digits(seconds);
      diff -= seconds * this.timeConst.secMs;

      let elapsed = !isNullOrEmpty(dd) ? dd : "";
      elapsed += !isNullOrEmpty(elapsed) ? (!isNullOrEmpty(hh) ? ":" + hh : "") : "";
      elapsed += (!isNullOrEmpty(elapsed) ? ":" + mm : mm) + (":" + ss);

      return elapsed;
    },
    ...mapActions([
      'refreshToken',
    ]),
  },
  watch: {
    refreshSession: async function(newValue, oldValue) {
      if (!this.sessionIsActive || newValue === oldValue || !newValue) {
        return;
      }

      this.refreshSession = false;
      // We've reached the max number of renewals, so return and do nothing.
      if (this.numberOfSessionsRenewed >= this.maximumRenewals) {
        return;
      }

      if (this.canAutoRenew) {
        if (this.isDevelopment) {
          this.showModal = true;
        }
        await this.requestNewToken();
        this.autoRenewed = true;
      }
      else {
        this.autoRenewed = false;
        this.countdown = this.sessionTimeLeft();
        this.showModal = true;
        this.intervalTimer = setInterval(() => {
          this.countdown -= this.timeConst.secMs;
        }, this.timeConst.secMs);
      }
    },
    sessionIsActive: function(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      if (newValue) {
        this.setSession();
      }
      else {
        clearTimeout(this.sessionTimer);
      }
    },
  },
};
</script>
